<template>
  <section class="page-main-banner" data-buried="Banner">
    <h1 class="page-main-banner-title font-dt pc-only">魔珐有言-3D数字人AI视频平台</h1>
    <h1 class="page-main-banner-title font-dt m-only">魔珐有言<br />3D数字人AI视频平台</h1>
    <div class="page-main-banner-sub pc-only">
      <p>
        无需拍摄剪辑，只需输入文字，即可一键生成高质量3D数字人视频。AI自动生成场景、灯光、人物和运镜，还能精准掌控每一个视频细节。
      </p>
      <p>高质量、高效率、低门槛、全流程可控，打造你专属的视频内容生产力。</p>
    </div>
    <div class="page-main-banner-sub m-only">
      <p>
        无需拍摄剪辑，只需输入文字，即可一键生成高质量3D数字人视频。AI自动生成场景、灯光、人物和运镜，还能精准掌控每一个视频细节。高质量、高效率、低门槛、全流程可控，打造你专属的视频内容生产力。
      </p>
    </div>

    <div class="page-main-banner-buttons">
      <ArrowBtn class="start-btn" variant="black-common" text="免费试用" @click="$emit('trial')" />
      <BaseBtn class="book-btn" variant="white-common" @click="$emit('consult')">
        <span>企业咨询</span>
      </BaseBtn>
    </div>
    <div
      ref="videoContainer"
      class="page-main-banner-video"
      :class="{ 'is-scaling': isScaling }"
      :style="videoStyle"
    >
      <VideoPlayer
        class="video_cover"
        :autoplay="true"
        :muted="localMuted"
        :enable-fullscreen="false"
        :poster-transform="undefined"
        fetchpriority="high"
        :poster="
          getFeUrl(
            '/youyan-platform/main/banner/banner-3/landing.jpg?x-oss-process=image/format,webp',
          )
        "
        :url="getFeUrl('/youyan-platform/main/banner/banner-3/output.m3u8')"
        objectFit="cover"
        title="无需拍摄剪辑，只需输入文字，使用有言即可一键生成高质量3D数字人视频"
      />
    </div>
    <div ref="marqueeContainer" class="marquee-container">
      <div class="marquee-title flex items-center">1000+家头部企业的选择</div>
      <div class="marquee-content flex-1 overflow-hidden">
        <div ref="marqueeWrapper" class="marquee-wrapper">
          <img
            class="marqueeWrapper__item"
            :src="getFeUrl('/youyan-platform/main/banner/logo-wall.svg')"
            alt="partner"
          />
          <img
            class="marqueeWrapper__item"
            :src="getFeUrl('/youyan-platform/main/banner/logo-wall.svg')"
            alt="partner"
          />
          <img
            class="marqueeWrapper__item"
            :src="getFeUrl('/youyan-platform/main/banner/logo-wall.svg')"
            alt="partner"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { useBreakpoint } from '@rendora/design/index';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import VideoPlayer from '~/components/video-player/index.vue';
import ArrowBtn from '~/design/components/buttons/arrow-btn.vue';
import BaseBtn from '~/design/components/buttons/base.vue';
import { getFeUrl } from '~/utils/url';

const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);

gsap.registerPlugin(ScrollTrigger);

const isMuted = inject<Ref<boolean>>('isMuted');
const localMuted = ref(isMuted?.value ?? true);

// 监听 isMuted 的变化，同步到本地状态
watch(
  () => isMuted?.value,
  newValue => {
    if (newValue !== undefined) {
      localMuted.value = newValue;
    }
  },
);
// Marquee loop logic
const marqueeWrapper = ref<HTMLElement>();

// Video scaling logic
const videoContainer = ref<HTMLElement>();
const isScaling = ref(false);
const videoStyle = ref({});

let tl: gsap.core.Timeline | null = null;

const gsapVideoElement = () => {
  if (!videoContainer.value) return;
  const videoElement = videoContainer.value;

  // 动态计算 targetWidth，在 1200-1440 之间
  const getTargetWidth = () => {
    const windowWidth = window.innerWidth;
    const minWidth = 1200;
    const maxWidth = 1440;
    // 根据窗口宽度动态计算
    const ratio = Math.min(Math.max((windowWidth - 1600) / (1920 - 1600), 0), 1);
    return Math.round(minWidth + (maxWidth - minWidth) * ratio);
  };

  // 根据宽度等比例计算高度 (16:9 比例)
  const getTargetHeight = (width: number) => {
    return Math.round((width * 9) / 16);
  };

  // 获取窗口尺寸的函数
  const getWindowSize = () => ({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  let { width: windowWidth, height: windowHeight } = getWindowSize();

  // 设置初始状态 - 视频全宽，通过transform居中
  gsap.set(videoElement, {
    width: '100%',
    height: '80vh',
    borderRadius: 0,
    transformOrigin: 'center center',
    // 通过 transform 让全宽视频相对于父容器居中
    x: `calc(-50vw + 50%)`,
    y: 0,
  });

  // 创建 ScrollTrigger 动画
  tl = gsap.timeline({
    scrollTrigger: {
      trigger: videoElement,
      start: 'bottom bottom',
      end: 'top 80px',
      scrub: 1,
      invalidateOnRefresh: true,
      onUpdate: self => {
        const progress = self.progress;
        isScaling.value = progress > 0 && progress < 1;
      },
      onRefresh: () => {
        const newWindowSize = getWindowSize();
        windowWidth = newWindowSize.width;
        windowHeight = newWindowSize.height;

        // 重新计算目标宽度和高度
        const targetWidth = getTargetWidth();
        const targetHeight = getTargetHeight(targetWidth);

        tl?.clear();

        tl?.fromTo(
          videoElement,
          {
            width: '100%',
            height: '80vh',
            borderRadius: 0,
            x: `calc(-50vw + 50%)`, // 初始状态：全宽居中
            y: 0,
          },
          {
            width: targetWidth,
            height: targetHeight,
            x: 0, // 目标状态：回到原始位置（已经是居中的）
            y: 0,
            borderRadius: 12,
            ease: 'sine.inOut',
            scrollTrigger: {
              trigger: videoElement,
              start: 'bottom bottom',
              end: 'top 80px',
              scrub: 1,
            },
          },
        );
      },
    },
  });

  // 添加缩放动画 - 使用动态计算的 targetWidth 和 targetHeight
  const initialTargetWidth = getTargetWidth();
  const initialTargetHeight = getTargetHeight(initialTargetWidth);

  // 添加缩放动画
  tl.fromTo(
    videoElement,
    {
      width: '100%',
      height: '80vh',
      borderRadius: 0,
      x: `calc(-50vw + 50%)`, // 初始：全宽但居中对齐
      y: 0,
    },
    {
      width: initialTargetWidth,
      height: initialTargetHeight,
      x: 0, // 目标：回到容器内正常位置（容器本身已经居中）
      y: 0,
      borderRadius: 12,
      ease: 'power2.inOut',
    },
  );

  // Marquee 跟随动画
  const marqueeContainer = document.querySelector('.marquee-container') as HTMLElement;
  if (marqueeContainer) {
    // 移除原有的 marginTop 动画，改为使用 CSS 固定 margin-top
    gsap.set(marqueeContainer, {
      marginTop: '40px', // 固定距离
    });
  }
};

const handleResize = () => {
  if (!isMobile.value) {
    if (tl?.scrollTrigger) {
      tl?.scrollTrigger.refresh();
    } else {
      gsapVideoElement();
    }
  } else {
    // 销毁正在运行的gsap动画
    // 销毁动画 & ScrollTrigger
    if (tl) {
      tl.kill();
      tl = null;
    }
    ScrollTrigger.getAll().forEach(t => t.kill());
    if (videoContainer.value) {
      gsap.set(videoContainer.value, { clearProps: 'all' });
      videoContainer.value.style.height = `${(window.innerWidth * 9) / 16}px`;
    }
  }
};

onMounted(() => {
  handleResize();
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  // 清理 ScrollTrigger 实例
  ScrollTrigger.getAll().forEach(trigger => trigger.kill());
});
</script>

<style lang="scss" scoped src="./styles/mobile.scss"></style>
<style lang="scss" scoped src="./styles/pc.scss"></style>
